import { ErrorMessage, ErrorMessageProps } from '@unistocks/components/error-message/error-message';
import { Loader, LoaderProps } from '@unistocks/components/loading/spinner/loader';
import { NoData, NoDataProps } from '@unistocks/components/no-data/no-data';
import { t } from '@unistocks/shared/localization/index.ts';
import { ReactNode } from 'react';
import { AnimatePresence, FontSizeTokens, SizeTokens, View, ViewProps } from 'tamagui';

export type AsyncVisualState = 'loading' | 'error' | 'empty' | null;

export type AsyncLoadingRenderProps = {
  loadingText?: LoaderProps['children'];
};

export type AsyncErrorRenderProps = {
  error?: ErrorMessageProps['error'];
  message?: ErrorMessageProps['message'];
  onRetry?: ErrorMessageProps['onRetry'];
};

export type AsyncStateProps = ViewProps & {
  size?: SizeTokens & FontSizeTokens;
  state: AsyncVisualState;
  loadingText?: LoaderProps['children'];
  /**
   * Custom element or render function used instead of the default `Loader` while `state` is `'loading'`.
   * When a function is provided, it receives `{ loadingText }`.
   */
  renderLoading?: ReactNode | ((props: AsyncLoadingRenderProps) => ReactNode);
  error?: ErrorMessageProps['error'];
  errorMessage?: ErrorMessageProps['message'];
  /**
   * Custom element or render function used instead of the default `ErrorMessage` while `state` is `'error'`.
   * When a function is provided, it receives `{ error, message, onRetry }`.
   */
  renderError?: ReactNode | ((props: AsyncErrorRenderProps) => ReactNode);
  onRetry?: ErrorMessageProps['onRetry'];
  noDataIcon?: NoDataProps['Icon'];
  noDataText?: NoDataProps['text'];
};

/**
 * A component that displays different UI states based on the provided `state` prop.
 * It can show a loading spinner, an error message, or a no data message depending on the state of the asynchronous operation.
 */
export const AsyncState: React.FC<AsyncStateProps> = ({
  state,
  loadingText = t('AbpUi::LoadingWithThreeDot'),
  renderLoading,
  error,
  errorMessage,
  renderError,
  onRetry,
  noDataIcon,
  noDataText,
  children,
  ...props
}) => (
  <AnimatePresence mode={'wait'}>
    {state === 'loading' ? (
      renderLoading ? (
        <View key={'loading'} {...baseProps} {...props}>
          {typeof renderLoading === 'function' ? renderLoading({ loadingText }) : renderLoading}
        </View>
      ) : (
        <Loader key={'loading'} {...baseProps} {...props}>
          {loadingText}
        </Loader>
      )
    ) : state === 'error' ? (
      renderError ? (
        <View key={'error-message'} {...baseProps} {...props}>
          {typeof renderError === 'function'
            ? renderError({ error, message: errorMessage, onRetry })
            : renderError}
        </View>
      ) : (
        <ErrorMessage
          key={'error-message'}
          error={error}
          message={errorMessage}
          onRetry={onRetry}
          {...baseProps}
          {...props}
        />
      )
    ) : state === 'empty' ? (
      <NoData
        key={'no-data'}
        text={noDataText}
        Icon={noDataIcon}
        size={'$7'}
        {...baseProps}
        {...props}
      />
    ) : (
      children
    )}
  </AnimatePresence>
);

AsyncState.displayName = 'AsyncState';

export const asyncStateTransition: ViewProps = {
  transition: 'quickLessBouncy',
  animateOnly: ['opacity', 'transform'],
  enterStyle: {
    o: 0,
    y: -20
  },
  exitStyle: {
    o: 0,
    y: -20
  }
};

const baseProps: ViewProps = {
  ...asyncStateTransition,
  flexGrow: 0,
  minH: null,
  py: '$4'
};
